import { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { searchSoldCardPrices } from '../api/cards';
import type { ChartPriceTransaction } from '../types';
import { formatUsd, median, platformMeta } from '../utils/market';

interface PriceChartProps {
  idLabel?: string;
  selectedPlatform?: string;
}

type TimeRange = 'all' | '1y' | '6m' | '3m' | '1m';

const TIME_RANGES: { key: TimeRange; label: string }[] = [
  { key: 'all', label: 'All time' },
  { key: '1y', label: '1 year' },
  { key: '6m', label: '6 months' },
  { key: '3m', label: '3 months' },
  { key: '1m', label: '1 month' },
];

interface UsdChartPoint extends ChartPriceTransaction {
  priceUsd: number;
  soldTime: number;
}

interface TooltipPoint {
  color: string;
  seriesName: string;
  value: [number, number];
}

export default function PriceChart({ idLabel, selectedPlatform }: PriceChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('3m');
  const [transactions, setTransactions] = useState<ChartPriceTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const chartInstanceRef = useRef<{ resize: (options?: { width?: number }) => void } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!idLabel) {
      setTransactions([]);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    searchSoldCardPrices(idLabel, timeRange)
      .then((response) => {
        if (!active) return;
        if (response.code === 200) {
          setTransactions(response.data || []);
        } else {
          setTransactions([]);
          setError(response.message || 'Price history could not be loaded.');
        }
      })
      .catch((requestError) => {
        if (!active) return;
        setTransactions([]);
        setError(requestError?.response?.data?.message || 'Price history could not be loaded.');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [idLabel, retryVersion, timeRange]);

  const chart = useMemo(() => {
    const allUsdPoints = transactions.filter((transaction): transaction is UsdChartPoint =>
      typeof transaction.priceUsd === 'number'
      && Number.isFinite(transaction.priceUsd)
      && typeof transaction.soldTime === 'number'
      && Number.isFinite(transaction.soldTime)
    );
    const visiblePoints = (selectedPlatform
      ? allUsdPoints.filter((transaction) => transaction.platform === selectedPlatform)
      : allUsdPoints
    ).sort((a, b) => a.soldTime - b.soldTime);

    const prices = visiblePoints.map((point) => point.priceUsd);
    const low = prices.length ? Math.min(...prices) : null;
    const high = prices.length ? Math.max(...prices) : null;
    const medianUsd = median(prices);
    const fxAsOf = visiblePoints.find((point) => point.fxAsOf)?.fxAsOf || null;
    const grouped = new Map<string, UsdChartPoint[]>();
    for (const point of visiblePoints) {
      const bucket = grouped.get(point.platform) || [];
      bucket.push(point);
      grouped.set(point.platform, bucket);
    }

    const series = Array.from(grouped, ([platform, points]) => {
      const meta = platformMeta(platform);
      return {
        name: platform,
        type: 'line' as const,
        smooth: 0.25,
        showSymbol: points.length < 12,
        symbolSize: 6,
        sampling: 'lttb' as const,
        lineStyle: { width: 2.5, color: meta.color },
        itemStyle: { color: meta.color },
        emphasis: { focus: 'series' as const },
        data: points.map((point) => [point.soldTime, point.priceUsd]),
        markPoint: {
          symbol: 'circle',
          symbolSize: 9,
          label: { show: false },
          data: [
            ...(high !== null && points.some((point) => point.priceUsd === high) ? [{ name: 'High', type: 'max' as const }] : []),
            ...(low !== null && points.some((point) => point.priceUsd === low) ? [{ name: 'Low', type: 'min' as const }] : []),
          ],
        },
      };
    });

    return {
      visiblePoints,
      medianUsd,
      low,
      high,
      fxAsOf,
      option: {
        animationDuration: 350,
        tooltip: {
          trigger: 'axis' as const,
          backgroundColor: '#080d18',
          borderWidth: 0,
          padding: 12,
          textStyle: { color: '#fff', fontFamily: 'Avenir Next, sans-serif' },
          formatter: (rawParams: unknown) => {
            const params = (Array.isArray(rawParams) ? rawParams : []) as TooltipPoint[];
            if (!params.length) return '';
            const date = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(params[0].value[0]));
            const lines = params.map((point) => `<div style="display:flex;align-items:center;justify-content:space-between;gap:20px;margin-top:6px"><span><i style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${point.color};margin-right:7px"></i>${platformMeta(point.seriesName).name}</span><b>${formatUsd(point.value[1])}</b></div>`);
            return `<div style="font-size:11px;color:rgba(255,255,255,.55)">${date}</div>${lines.join('')}`;
          },
        },
        legend: {
          data: Array.from(grouped.keys()),
          bottom: 0,
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: { color: '#5f6b7a', fontSize: 11 },
          formatter: (platform: string) => platformMeta(platform).name,
        },
        grid: { left: 8, right: 12, bottom: 42, top: 18, containLabel: true },
        xAxis: {
          type: 'time' as const,
          axisLine: { lineStyle: { color: '#dce3ec' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#8a96a6',
            fontSize: 10,
            formatter: (value: number) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(value)),
          },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value' as const,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#8a96a6', fontSize: 10, formatter: (value: number) => `$${value.toLocaleString()}` },
          splitLine: { lineStyle: { color: '#edf1f5' } },
        },
        series,
      },
    };
  }, [selectedPlatform, transactions]);

  const unconvertedTransactions = transactions.filter((transaction) => (
    (!selectedPlatform || transaction.platform === selectedPlatform)
    && Number.isFinite(transaction.price)
    && !Number.isFinite(transaction.priceUsd)
  ));
  const retryableFxCount = unconvertedTransactions.filter((transaction) => Boolean(transaction.currency)).length;
  const missingCurrencyCount = unconvertedTransactions.length - retryableFxCount;
  const unconvertedCount = unconvertedTransactions.length;
  const hasRawButUnconverted = unconvertedCount > 0 && chart.visiblePoints.length === 0;

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const resize = () => {
      const chartRoot = container.querySelector<HTMLElement>('.echarts-for-react');
      if (chartRoot?.clientWidth) {
        chartInstanceRef.current?.resize({ width: chartRoot.clientWidth });
      }
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    window.addEventListener('resize', resize);
    const frame = window.requestAnimationFrame(resize);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [chart.visiblePoints.length, selectedPlatform, timeRange]);

  return (
    <section className="overflow-hidden rounded-[24px] border border-border bg-white shadow-sm" aria-labelledby="price-history-heading">
      <div className="border-b border-border px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="eyebrow text-cyan">USD price history</p>
            <h2 id="price-history-heading" className="mt-2 text-xl font-semibold tracking-[-0.035em] text-primary">Sold-price trend</h2>
            <p className="mt-1 text-xs text-secondary">Every plotted value is normalized to USD{chart.fxAsOf ? ` · FX ${chart.fxAsOf}` : ''}.</p>
          </div>
          <div className="flex flex-wrap gap-1.5" aria-label="Price history range">
            {TIME_RANGES.map((range) => (
              <button
                key={range.key}
                onClick={() => setTimeRange(range.key)}
                className={`rounded-full px-3 py-1.5 text-[10px] font-bold transition-colors ${timeRange === range.key ? 'bg-ink text-white' : 'border border-border bg-white text-secondary hover:border-cyan/40'}`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {chart.visiblePoints.length > 0 && (
          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ['Median', formatUsd(chart.medianUsd)],
              ['Low', formatUsd(chart.low)],
              ['High', formatUsd(chart.high)],
              ['USD sales', chart.visiblePoints.length.toLocaleString()],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl bg-background-secondary px-3 py-2.5">
                <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-secondary-light">{label}</span>
                <span className="price-numeral mt-1 block text-sm font-bold text-primary">{value}</span>
              </div>
            ))}
          </div>
        )}
        {chart.visiblePoints.length > 0 && unconvertedCount > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber/25 bg-amber/10 px-4 py-3" role="status">
            <p className="text-xs text-amber-900">
              {unconvertedCount} point(s) are excluded: {retryableFxCount} waiting for FX, {missingCurrencyCount} missing source currency.
            </p>
            {retryableFxCount > 0 ? (
              <button onClick={() => setRetryVersion((version) => version + 1)} className="rounded-xl border border-amber/30 bg-white px-4 py-2 text-xs font-bold text-amber-900">Retry chart USD</button>
            ) : (
              <a href="#sold-evidence-heading" className="rounded-xl border border-amber/30 bg-white px-4 py-2 text-xs font-bold text-amber-900">Inspect raw evidence</a>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-80 items-center justify-center">
          <div className="text-center"><div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-border border-t-cyan" /><p className="mt-3 text-sm text-secondary">Loading USD history…</p></div>
        </div>
      ) : error ? (
        <div className="flex h-80 items-center justify-center px-6 text-center">
          <div>
            <p className="text-sm text-red-600">{error}</p>
            <button onClick={() => setRetryVersion((version) => version + 1)} className="mt-4 rounded-xl bg-red-700 px-4 py-2 text-xs font-bold text-white">Retry price history</button>
          </div>
        </div>
      ) : chart.visiblePoints.length > 0 ? (
        <div ref={chartContainerRef} className="min-w-0 px-2 pb-3 pt-2 sm:px-4">
          <ReactECharts
            option={chart.option}
            notMerge
            style={{ height: 350, width: '100%' }}
            onChartReady={(instance) => {
              chartInstanceRef.current = instance;
              window.requestAnimationFrame(() => {
                const chartRoot = chartContainerRef.current?.querySelector<HTMLElement>('.echarts-for-react');
                if (chartRoot?.clientWidth) instance.resize({ width: chartRoot.clientWidth });
              });
            }}
          />
        </div>
      ) : (
        <div className="flex h-80 items-center justify-center px-6 text-center">
          <div>
            <p className="text-base font-semibold text-primary">{hasRawButUnconverted ? 'USD conversion is temporarily unavailable' : 'No sold-price data in this range'}</p>
            <p className="mt-2 text-sm text-secondary">{hasRawButUnconverted ? 'Raw values were preserved and will not be mislabeled as USD.' : 'Try a wider time range or another marketplace.'}</p>
            {hasRawButUnconverted && retryableFxCount > 0 && (
              <button onClick={() => setRetryVersion((version) => version + 1)} className="mt-4 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-white">Retry USD conversion</button>
            )}
            {hasRawButUnconverted && retryableFxCount === 0 && missingCurrencyCount > 0 && (
              <a href="#sold-evidence-heading" className="mt-4 inline-flex rounded-xl bg-primary px-4 py-2 text-xs font-bold text-white">Inspect raw evidence</a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
